import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { subscriptionAPI } from '../../api';
import { Sidebar } from '../dashboard_component';

function AllInvitationsDashboard() {
  const [invitations, setInvitations] = useState([]);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchAllInvitations();
  }, []);

  const fetchAllInvitations = async () => {
    setIsLoading(true);
    try {
      // Fetch all invitations - we'll need to create a new API endpoint for this
      const invitationsRes = await subscriptionAPI.getAllInvitations();
      console.log('All invitations response:', invitationsRes);
      
      if (invitationsRes.data?.invitations) {
        setInvitations(invitationsRes.data.invitations || []);
      } else if (invitationsRes.data && Array.isArray(invitationsRes.data)) {
        setInvitations(invitationsRes.data || []);
      } else {
        setInvitations([]);
      }
    } catch (error) {
      console.error('Error loading invitations:', error);
      const errorMessage = error.response?.data?.detail || 
                          error.data?.detail || 
                          error.message || 
                          'Failed to load invitations';
      toast.error(errorMessage);
      setInvitations([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAcceptInvitation = async (invitationId) => {
    setIsLoading(true);
    try {
      const response = await subscriptionAPI.acceptInvitation(invitationId);
      console.log('Accept invitation response:', response);
      
      if (response.success || response.message) {
        const successMessage = response.message || 'Invitation accepted!';
        toast.success(successMessage);
        fetchAllInvitations(); // Refresh the list
      } else {
        const errorMessage = response.error || response.detail || 'Failed to accept invitation';
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error('Error accepting invitation:', error);
      const errorMessage = error.response?.data?.detail || 
                          error.data?.detail || 
                          error.message || 
                          'Failed to accept invitation';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRejectInvitation = async (invitationId) => {
    if (!window.confirm('Are you sure you want to reject this invitation?')) return;
    
    setIsLoading(true);
    try {
      const response = await subscriptionAPI.rejectInvitation(invitationId);
      
      if (response.message) {
        toast.success(response.message);
        fetchAllInvitations(); // Refresh the list
      } else {
        toast.error('Failed to reject invitation');
      }
    } catch (error) {
      console.error('Error rejecting invitation:', error);
      const errorMessage = error.response?.data?.detail || 
                          error.data?.detail || 
                          error.message || 
                          'Failed to reject invitation';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLeaveSubscription = async (subscriptionId) => {
    if (!window.confirm('Are you sure you want to leave this subscription?')) return;
    
    setIsLoading(true);
    try {
      const response = await subscriptionAPI.leaveSubscription(subscriptionId);
      
      if (response.message) {
        toast.success(response.message);
        fetchAllInvitations(); // Refresh the list
      } else {
        toast.error('Failed to leave subscription');
      }
    } catch (error) {
      console.error('Error leaving subscription:', error);
      const errorMessage = error.response?.data?.detail || 
                          error.data?.detail || 
                          error.message || 
                          'Failed to leave subscription';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusBadge = (status, isExpired = false) => {
    const baseClasses = "px-3 py-1 rounded-full text-sm font-medium";
    
    if (isExpired) {
      return (
        <span className={`${baseClasses} bg-red-100 text-red-800 border border-red-200`}>
          <i className="fas fa-exclamation-triangle mr-1"></i>
          Expired
        </span>
      );
    }
    
    switch(status?.toLowerCase()) {
      case 'pending':
        return (
          <span className={`${baseClasses} bg-yellow-100 text-yellow-800 border border-yellow-200`}>
            <i className="fas fa-clock mr-1"></i>
            Pending
          </span>
        );
      case 'accepted':
        return (
          <span className={`${baseClasses} bg-green-100 text-green-800 border border-green-200`}>
            <i className="fas fa-check-circle mr-1"></i>
            Accepted
          </span>
        );
      case 'rejected':
        return (
          <span className={`${baseClasses} bg-red-100 text-red-800 border border-red-200`}>
            <i className="fas fa-times-circle mr-1"></i>
            Rejected
          </span>
        );
      default:
        return (
          <span className={`${baseClasses} bg-gray-100 text-gray-800 border border-gray-200`}>
            <i className="fas fa-question-circle mr-1"></i>
            {status || 'Unknown'}
          </span>
        );
    }
  };

  const handleToggle = () => {
    setIsCollapsed(!isCollapsed);
  };

  // Filter invitations by status
  const pendingInvitations = invitations.filter(inv => 
    inv.status === 'pending' && 
    (!inv.expires_at || new Date(inv.expires_at) > new Date())
  );
  
  const acceptedInvitations = invitations.filter(inv => inv.status === 'accepted');
  const expiredInvitations = invitations.filter(inv => 
    inv.status === 'pending' && 
    inv.expires_at && 
    new Date(inv.expires_at) < new Date()
  );
  const rejectedInvitations = invitations.filter(inv => inv.status === 'rejected');

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-gray-50 to-blue-50/30">
      <Sidebar isCollapsed={isCollapsed} onToggle={handleToggle} />
      
      <div 
        className={`flex-1 transition-all duration-300 pt-16 lg:pt-8 ${
          isCollapsed ? 'lg:ml-20' : 'lg:ml-64'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <ToastContainer />
          
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-800">My Subscription Invitations</h1>
            <p className="text-gray-600 mt-2">View and manage all your subscription invitations in one place</p>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-yellow-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm">Pending</p>
                  <p className="text-2xl font-bold text-gray-800 mt-1">{pendingInvitations.length}</p>
                </div>
                <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                  <i className="fas fa-clock text-yellow-600 text-xl"></i>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-green-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm">Accepted</p>
                  <p className="text-2xl font-bold text-gray-800 mt-1">{acceptedInvitations.length}</p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <i className="fas fa-check-circle text-green-600 text-xl"></i>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-red-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm">Expired</p>
                  <p className="text-2xl font-bold text-gray-800 mt-1">{expiredInvitations.length}</p>
                </div>
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <i className="fas fa-exclamation-triangle text-red-600 text-xl"></i>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-gray-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm">Rejected</p>
                  <p className="text-2xl font-bold text-gray-800 mt-1">{rejectedInvitations.length}</p>
                </div>
                <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                  <i className="fas fa-times-circle text-gray-600 text-xl"></i>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Pending Invitations Column */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
                <div className="p-6 border-b border-gray-200">
                  <h2 className="text-xl font-bold text-gray-800">
                    Pending Invitations ({pendingInvitations.length})
                  </h2>
                  <p className="text-gray-600 text-sm mt-1">Invitations waiting for your response</p>
                </div>
                
                <div className="p-6">
                  {isLoading ? (
                    <div className="flex justify-center items-center py-12">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                    </div>
                  ) : pendingInvitations.length === 0 ? (
                    <div className="text-center py-12">
                      <div className="mx-auto w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                        <i className="fas fa-inbox text-gray-400 text-3xl"></i>
                      </div>
                      <p className="text-gray-500 text-lg mb-2">No pending invitations</p>
                      <p className="text-gray-400 text-sm">You don't have any pending invitations at the moment.</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {pendingInvitations.map((invitation) => (
                        <div key={invitation.id} className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow duration-200">
                          <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start gap-4">
                            <div className="flex-1">
                              <div className="flex items-start mb-3">
                                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                                  <i className="fas fa-user-tag text-blue-600"></i>
                                </div>
                                <div>
                                  <h3 className="font-bold text-gray-800 text-lg">
                                    Invitation from {invitation.owner_name || 'Subscription Owner'}
                                  </h3>
                                  <p className="text-gray-600">{invitation.owner_email}</p>
                                </div>
                              </div>
                              
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-gray-500 mb-4">
                                <div className="flex items-center">
                                  <i className="fas fa-calendar-alt text-blue-500 mr-2"></i>
                                  <span>Invited: {new Date(invitation.invited_at || invitation.created_at).toLocaleString()}</span>
                                </div>
                                <div className="flex items-center">
                                  <i className="fas fa-clock text-amber-500 mr-2"></i>
                                  <span>Expires: {new Date(invitation.expires_at).toLocaleString()}</span>
                                </div>
                              </div>
                              
                              <div className="mb-4">
                                {getStatusBadge(invitation.status)}
                              </div>
                            </div>
                            
                            <div className="flex flex-col sm:flex-row lg:flex-col gap-2">
                              <button
                                onClick={() => handleAcceptInvitation(invitation.id)}
                                disabled={isLoading}
                                className="bg-green-600 hover:bg-green-700 text-white px-4 py-3 rounded-lg font-semibold disabled:opacity-50 transition-colors duration-200 flex items-center justify-center"
                              >
                                <i className="fas fa-check mr-2"></i>
                                Accept Invitation
                              </button>
                              <button
                                onClick={() => handleRejectInvitation(invitation.id)}
                                disabled={isLoading}
                                className="bg-red-600 hover:bg-red-700 text-white px-4 py-3 rounded-lg font-semibold disabled:opacity-50 transition-colors duration-200 flex items-center justify-center"
                              >
                                <i className="fas fa-times mr-2"></i>
                                Reject
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Expired Invitations */}
              {expiredInvitations.length > 0 && (
                <div className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
                  <div className="p-6 border-b border-gray-200">
                    <h2 className="text-xl font-bold text-gray-800">
                      Expired Invitations ({expiredInvitations.length})
                    </h2>
                    <p className="text-gray-600 text-sm mt-1">Invitations that are no longer valid</p>
                  </div>
                  
                  <div className="p-6">
                    <div className="space-y-4">
                      {expiredInvitations.map((invitation) => (
                        <div key={invitation.id} className="border border-red-200 rounded-lg p-6 bg-red-50">
                          <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start gap-4">
                            <div className="flex-1">
                              <div className="flex items-start mb-3">
                                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mr-4">
                                  <i className="fas fa-user-tag text-red-600"></i>
                                </div>
                                <div>
                                  <h3 className="font-bold text-gray-800 text-lg">
                                    Expired: {invitation.owner_name || 'Subscription Owner'}
                                  </h3>
                                  <p className="text-gray-600">{invitation.owner_email}</p>
                                </div>
                              </div>
                              
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-gray-500 mb-4">
                                <div className="flex items-center">
                                  <i className="fas fa-calendar-alt text-blue-500 mr-2"></i>
                                  <span>Invited: {new Date(invitation.invited_at || invitation.created_at).toLocaleString()}</span>
                                </div>
                                <div className="flex items-center">
                                  <i className="fas fa-clock text-red-500 mr-2"></i>
                                  <span>Expired: {new Date(invitation.expires_at).toLocaleString()}</span>
                                </div>
                              </div>
                              
                              <div className="mb-4">
                                {getStatusBadge(invitation.status, true)}
                              </div>
                            </div>
                            
                            <div className="flex flex-col sm:flex-row lg:flex-col gap-2">
                              <button
                                disabled
                                className="bg-gray-400 text-white px-4 py-3 rounded-lg font-semibold flex items-center justify-center cursor-not-allowed"
                              >
                                <i className="fas fa-exclamation-triangle mr-2"></i>
                                Expired
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Accepted Invitations Column */}
            <div>
              <div className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
                <div className="p-6 border-b border-gray-200">
                  <h2 className="text-xl font-bold text-gray-800">
                    Active Subscriptions ({acceptedInvitations.length})
                  </h2>
                  <p className="text-gray-600 text-sm mt-1">Subscriptions you've joined</p>
                </div>
                
                <div className="p-6">
                  {isLoading ? (
                    <div className="flex justify-center items-center py-12">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
                    </div>
                  ) : acceptedInvitations.length === 0 ? (
                    <div className="text-center py-12">
                      <div className="mx-auto w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                        <i className="fas fa-users text-gray-400 text-3xl"></i>
                      </div>
                      <p className="text-gray-500 text-lg mb-2">No active subscriptions</p>
                      <p className="text-gray-400 text-sm">You haven't joined any subscriptions yet.</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {acceptedInvitations.map((invitation) => (
                        <div key={invitation.id} className="border border-green-200 rounded-lg p-6 bg-green-50">
                          <div className="flex items-start mb-3">
                            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mr-4">
                              <i className="fas fa-user-check text-green-600"></i>
                            </div>
                            <div className="flex-1">
                              <h3 className="font-bold text-gray-800 text-lg">
                                {invitation.owner_name || 'Subscription Owner'}
                              </h3>
                              <p className="text-gray-600 text-sm">{invitation.owner_email}</p>
                            </div>
                          </div>
                          
                          <div className="space-y-2 text-sm text-gray-600 mb-4">
                            <div className="flex items-center">
                              <i className="fas fa-calendar-check text-green-500 mr-2"></i>
                              <span>Joined: {new Date(invitation.joined_at || invitation.updated_at).toLocaleDateString()}</span>
                            </div>
                            {invitation.subscription_expires && (
                              <div className="flex items-center">
                                <i className="fas fa-clock text-amber-500 mr-2"></i>
                                <span>Expires: {new Date(invitation.subscription_expires).toLocaleDateString()}</span>
                              </div>
                            )}
                          </div>
                          
                          <div className="mb-4">
                            {getStatusBadge(invitation.status)}
                          </div>
                          
                          <button
                            onClick={() => handleLeaveSubscription(invitation.id || invitation.id)}
                            disabled={isLoading}
                            className="w-full bg-red-600 hover:bg-red-700 text-white px-4 py-3 rounded-lg font-semibold disabled:opacity-50 transition-colors duration-200 flex items-center justify-center"
                          >
                            <i className="fas fa-sign-out-alt mr-2"></i>
                            Leave Subscription
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Info Box */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                <h3 className="text-lg font-bold text-blue-800 mb-3 flex items-center">
                  <i className="fas fa-info-circle mr-2"></i>
                  How subscription sharing works:
                </h3>
                <ul className="list-disc list-inside text-blue-700 space-y-2 text-sm">
                  <li>Accept invitations to join someone else's subscription plan</li>
                  <li>You'll get access to premium features while the subscription is active</li>
                  <li>You can leave a subscription anytime from your dashboard</li>
                  <li>If the owner cancels their subscription, all shared users lose access</li>
                  <li>You can only be part of one subscription at a time</li>
                  <li>Invitations expire after 7 days if not accepted</li>
                </ul>
              </div>

              {/* Action Buttons */}
              <div className="mt-8">
                <div className="flex flex-col gap-4">
                  <a
                    href="https://wa.me/2348113315252"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex items-center justify-center gap-3 bg-green-500 hover:bg-green-600 text-white py-3.5 px-6 rounded-xl font-semibold transition-colors duration-300"
                  >
                    <i className="fab fa-whatsapp text-lg"></i>
                    <span>Chat on WhatsApp</span>
                  </a>

                  <button
                    onClick={() => navigate('/cbt_creator/dashboard')}
                    className="group flex items-center justify-center gap-3 bg-blue-600 hover:bg-blue-700 text-white py-3.5 px-6 rounded-xl font-semibold transition-colors duration-300"
                  >
                    <i className="fas fa-arrow-left text-lg"></i>
                    <span>Back to Dashboard</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AllInvitationsDashboard;
// [file content end]#
